import { forEach, map } from "lodash";
import { observable, action, runInAction, computed } from "mobx";
import { RouterStore } from "mobx-react-router";
import { camel, titleCase, snakeCase } from "change-case";

import PageStore from "app/PageStore";
import Store from "lib/models/Store";
import { SortDirection } from "lib/enums";
import { formatQueryString, parseQueryString } from "lib/formatter";

import NewsService, { NewsSearchResult } from "./NewsService";
import { News, NewsSource, MajorTag, Tag, NewsSummarySource } from "./models";
import NewsFilter from "./commands/NewsFilter";
import NewsForm from "./commands/NewsForm";
import AddTagForm from "./commands/AddTagForm";

export default class NewsStore extends Store<News, NewsSource> {
  @observable public loading: boolean = false;
  @observable public detailLoading: boolean = false;
  @observable public dismissLoading: boolean = false;
  @observable public filter: NewsFilter;
  @observable public form: NewsForm;
  @observable public addTagForm: AddTagForm;
  @observable public majorTags: { [key: string]: MajorTag[] } = {};
  @observable public tags: Tag[] = [];
  @observable public sources: string[] = [];

  @observable private searchResult: NewsSearchResult;
  @observable private selectedId?: number;

  @observable private visaTypeIds: number[];
  @observable private topicIds: number[];
  @observable private lawfullysPickIds: number[];
  @observable private summary: NewsSummarySource;

  private pageStore: PageStore;
  private routerStore: RouterStore;
  private newsService: NewsService;

  private pageSize: number = 10;

  constructor(
    pageStore: PageStore,
    routerStore: RouterStore,
    newsService: NewsService,
  ) {
    super();
    this.pageStore = pageStore;
    this.routerStore = routerStore;
    this.newsService = newsService;

    this.resetForm();
    this.resetFilter();

    this.searchResult = {
      total: 0,
      page: 1,
      ids: [],
      filter: this.filter,
    };

    this.addTagForm = new AddTagForm();
  }

  @computed
  public get visaTypeOptions() {
    return !!this.tags && this.visaTypeIds
      ? this.tags
          .filter((x) => this.visaTypeIds.includes(x.majorId))
          .map((tag) => ({
            key: tag.id,
            label: tag.name,
            value: tag.id,
          }))
      : [];
  }

  @computed
  public get topicOptions() {
    return !!this.tags && this.topicIds
      ? this.tags
          .filter((x) => this.topicIds.includes(x.majorId))
          .map((tag) => ({
            key: tag.id,
            label: tag.name,
            value: tag.id,
          }))
      : [];
  }

  @computed
  public get lawfullysPickOptions() {
    return !!this.tags && this.lawfullysPickIds
      ? this.tags
          .filter((x) => this.lawfullysPickIds.includes(x.majorId))
          .map((tag) => ({
            key: tag.id,
            label: tag.name,
            value: tag.id,
          }))
      : [];
  }

  @computed
  public get newsOptions() {
    return !!this.summary && this.summary.allNews
      ? Object.keys(this.summary.allNews).map((key) => ({
          key,
          label: `${titleCase(key)} (${this.summary.allNews[
            key
          ].toLocaleString()})`,
          value: snakeCase(key),
        }))
      : [];
  }

  @computed
  public get trackerAppOptions() {
    return !!this.summary && this.summary.trackerApp
      ? Object.keys(this.summary.trackerApp).map((key) => ({
          key,
          label: `${titleCase(key)} (${this.summary.trackerApp[
            key
          ].toLocaleString()})`,
          value: snakeCase(key),
        }))
      : [];
  }

  @computed
  public get total() {
    return this.searchResult.total;
  }

  @computed
  public get page() {
    return this.searchResult.page;
  }

  @computed
  get news(): News[] {
    return map(this.searchResult.ids, (x) => this.entities[x]);
  }

  @computed
  get firstNewsId(): number | undefined {
    return this.searchResult.ids.length > 0
      ? Number(this.searchResult.ids[0])
      : undefined;
  }

  @computed
  get lastNewsId(): number | undefined {
    return this.searchResult.ids.length > 0
      ? Number(this.searchResult.ids[this.news.length - 1])
      : undefined;
  }

  @computed
  get isFirstNews(): boolean {
    return !!this.selectedId && this.selectedId === this.firstNewsId;
  }

  @computed
  get isLastNews(): boolean {
    return !!this.selectedId && this.selectedId === this.lastNewsId;
  }

  @action.bound
  public async listMajorTag() {
    const { openToast } = this.pageStore;
    this.detailLoading = true;
    try {
      const res = await this.newsService.findMajorTags();
      if (!res) {
        throw res;
      }
      runInAction(() => {
        const majorTags = {};
        res
          .filter((tag) => tag.isActive)
          .sort((a, b) => a.order - b.order)
          .forEach((tag) => {
            const key = camel(tag.newsType);
            if (!majorTags[key]) {
              majorTags[key] = [];
            }
            majorTags[key].push(tag);
          });
        this.majorTags = majorTags;
        this.visaTypeIds = res
          .filter((x) => x.newsType === "VISA_TYPE")
          .map((x) => Number(x.id));
        this.topicIds = res
          .filter((x) => x.newsType === "TOPIC")
          .map((x) => Number(x.id));
        this.lawfullysPickIds = res
          .filter((x) => x.newsType === "LAWFULLY_PICK")
          .map((x) => Number(x.id));
      });
    } catch (e) {
      openToast(e.message, { type: "error" });
    } finally {
      runInAction(() => {
        this.detailLoading = false;
      });
    }
  }

  @action.bound
  public async listTag() {
    const { openToast } = this.pageStore;
    this.detailLoading = true;
    try {
      const res = await this.newsService.findTags();
      if (!res) {
        throw res;
      }
      runInAction(() => {
        this.tags = res;
      });
    } catch (e) {
      openToast(e.message, { type: "error" });
    } finally {
      runInAction(() => {
        this.detailLoading = false;
      });
    }
  }

  @action.bound
  public async listSource() {
    const { openSpinner, openToast, closeSpinner } = this.pageStore;
    openSpinner();
    try {
      const res = await this.newsService.findSources();
      if (!res) {
        throw res;
      }
      runInAction(() => {
        this.sources = res;
      });
    } catch (e) {
      openToast(e.message, { type: "error" });
    } finally {
      closeSpinner();
    }
  }

  @action.bound
  public async listSummary() {
    const { openSpinner, openToast, closeSpinner } = this.pageStore;
    openSpinner();
    try {
      const res = await this.newsService.findSummary();
      if (!res) {
        throw res;
      }
      runInAction(() => {
        this.summary = res;
      });
    } catch (e) {
      openToast(e.message, { type: "error" });
    } finally {
      closeSpinner();
    }
  }

  @action.bound
  public async load(id: number) {
    this.selectedId = undefined;
    this.detailLoading = true;
    try {
      const res = await this.newsService.getNews(id);
      runInAction(() => {
        this.selectedId = id;
        this.form = new NewsForm();
        this.form.update({
          ...res.form,
        });
        this.form.initialize();
      });
    } catch (e) {
      runInAction(() => {
        this.selectedId = undefined;
        this.pageStore.openToast(e.message, { type: "error" });
      });
    } finally {
      runInAction(() => {
        this.detailLoading = false;
      });
    }
  }

  @action.bound
  public search() {
    this.routerStore.history.push({
      search: formatQueryString(this.filter, 1),
    });
    this.searchUsingService(this.filter, 1);
  }

  @action.bound
  public changePage(page: number) {
    this.routerStore.history.push({
      search: formatQueryString(this.searchResult.filter, page),
    });
    this.searchUsingService(this.searchResult.filter, page);
  }

  @action.bound
  public searchByLocation() {
    if (this.routerStore.location.search) {
      const queryForm = parseQueryString(
        this.routerStore.location.search,
        this.filter,
      );
      this.filter.update({
        ...queryForm.form,
      });
      this.validateAndSearch(queryForm.page);
    } else {
      this.validateAndSearch(1);
    }
  }

  @action.bound
  public async getPreview(url: string) {
    this.detailLoading = true;
    try {
      const res = await this.newsService.getNewsPreview(url);
      if (!res) {
        throw res;
      }
      runInAction(() => {
        this.form.update({
          originalUrl: url,
          title: res.title,
          publishedDate: res.publishedDate,
          imageUrl: res.imageUrl,
          source: res.source,
          sourceImageUrl: res.sourceImageUrl,
        });
      });
    } catch (e) {
      if (e.response.status === 409) {
        this.pageStore.openToast("Same URL is already exist", {
          type: "error",
        });
      } else {
        this.pageStore.openToast(e.message, { type: "error" });
      }
    } finally {
      this.detailLoading = false;
    }
  }

  @action.bound
  public async submit() {
    const { openSpinner, openToast, closeSpinner } = this.pageStore;
    openSpinner();
    try {
      const res = await this.newsService.addNews(this.form);
      if (!res) {
        throw res;
      }
    } catch (e) {
      openToast(e.message, { type: "error" });
    } finally {
      closeSpinner();
    }
  }

  @action.bound
  public async submitTag() {
    const { openSpinner, openToast, closeSpinner } = this.pageStore;
    openSpinner();
    try {
      const res = await this.newsService.addTag(this.addTagForm);
      if (!res) {
        throw res;
      }
    } catch (e) {
      openToast(e.message, { type: "error" });
    } finally {
      closeSpinner();
    }
  }

  @action.bound
  public async updateNews() {
    const { openToast } = this.pageStore;
    this.detailLoading = true;
    try {
      if (!this.selectedId) {
        throw new Error("Not valid news id");
      }
      const res = await this.newsService.updateNews(this.selectedId, this.form);
      if (!res) {
        throw res;
      }
      runInAction(() => {
        const { tags, isDismissed, summary } = this.form;
        const isChecked = !!summary || !!tags;
        this.form.update({
          isChecked,
        });
        this.form.initialize();
        const news = this.entities[this.selectedId!];
        const updates = {
          ...news.sources,
          tags,
          summary,
          isDismissed,
          isChecked,
        };
        this.update([updates]);
      });
    } catch (e) {
      openToast(e.message, { type: "error" });
    } finally {
      runInAction(() => {
        this.detailLoading = false;
      });
    }
  }

  @action.bound
  public async updateNewsDismiss() {
    const { openToast } = this.pageStore;
    this.dismissLoading = true;
    try {
      if (!this.selectedId) {
        throw new Error("Not valid news id");
      }
      const res = await this.newsService.dismissNews(this.selectedId);
      if (!res) {
        throw res;
      }
      runInAction(() => {
        this.form.update({
          isDismissed: true,
        });
        this.form.initialize();
        const news = this.entities[this.selectedId!];
        const updates = {
          ...news.sources,
          isDismissed: true,
        };
        this.update([updates]);
        openToast("Successfully dismiss this news", { type: "success" });
      });
    } catch (e) {
      openToast(e.message, { type: "error" });
    } finally {
      runInAction(() => {
        this.dismissLoading = false;
      });
    }
  }

  @action.bound
  public async updateNewsRestore() {
    const { openToast } = this.pageStore;
    this.dismissLoading = true;
    try {
      if (!this.selectedId) {
        throw new Error("Not valid news id");
      }
      const res = await this.newsService.restoreNews(this.selectedId);
      if (!res) {
        throw res;
      }
      runInAction(() => {
        this.form.update({
          isDismissed: false,
        });
        this.form.initialize();
        const news = this.entities[this.selectedId!];
        const updates = {
          ...news.sources,
          isDismissed: false,
        };
        this.update([updates]);
        openToast("Successfully restore this news", { type: "success" });
      });
    } catch (e) {
      openToast(e.message, { type: "error" });
    } finally {
      runInAction(() => {
        this.dismissLoading = false;
      });
    }
  }

  @action.bound
  public resetForm() {
    this.form = new NewsForm();
  }

  @action.bound
  public resetFilter() {
    this.filter = new NewsFilter({
      preset: [],
      orderBy: "created_at",
      sort: SortDirection.DESC,
    });
  }

  @action.bound
  protected async searchUsingService(filter: NewsFilter, page: number) {
    if (!this.loading) {
      const { openSpinner, closeSpinner } = this.pageStore;
      openSpinner();
      this.loading = true;
      this.searchResult.page = page || 1;
      const res = await this.newsService.findNews(filter, page, this.pageSize);
      runInAction(() => {
        closeSpinner();
        this.loading = false;
        this.searchResult = res.result;
        this.update(res.sources);
      });
    }
  }

  protected update(sources: NewsSource[]) {
    forEach(sources, (x) => {
      if (typeof this.entities[x.id] === "undefined") {
        this.entities[x.id] = new News(x.id, x);
      } else {
        this.entities[x.id].update(x);
      }
    });
  }

  private async validateAndSearch(pageNumber: number) {
    const validateResult = await this.filter.validate();
    if (validateResult) {
      this.searchUsingService(this.filter, pageNumber);
    } else {
      this.pageStore.closeSpinner();
      this.pageStore.openAlert({
        title: "일부 항목값이 올바르지 않습니다.",
        message: `다음 항목을 확인해주세요: ${this.filter.invalidFieldNames}`,
        isError: true,
      });
    }
  }
}
